import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { ThemeProvider } from './components/ThemeProvider'
import { DocsDrawerProvider } from './components/DocsDrawerContext'
import Navbar from './components/Navbar'
import ScrollToTop from './components/ScrollToTop'
import Home from './pages/Home'
import Docs from './pages/Docs'
import About from './pages/About'
import FAQ from './pages/FAQ'
import Feedback from './pages/Feedback'
import Privacy from './pages/Privacy'
import Projects from './pages/Projects'
import ProjectGuide from './pages/projects/ProjectGuide'
import NotFound from './pages/NotFound'
import Waitlist from './pages/Waitlist'
import Pricing from './pages/Pricing'
import Skills from './pages/Skills'
import PocketBaseComparison from './pages/comparisons/PocketBaseComparison'

import FlameWrap from './components/FlameWrap'

function AppContent() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrollProgress, setScrollProgress] = useState(1);

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    if (!isHome) return;
    const scrollTop = e.currentTarget.scrollTop;
    const startFade = window.innerHeight * 0.75;
    
    if (scrollTop < startFade) {
      setScrollProgress(1);
    } else {
      const fadeDistance = 1000;
      const progress = Math.max(0, 1 - ((scrollTop - startFade) / fadeDistance));
      setScrollProgress(progress);
    }
  };

  useEffect(() => {
    if (!isHome) {
      setScrollProgress(1);
    } else {
      const mainEl = document.getElementById('main-scroll-container');
      if (mainEl) {
        const scrollTop = mainEl.scrollTop;
        const startFade = window.innerHeight * 0.75;
        if (scrollTop < startFade) {
          setScrollProgress(1);
        } else {
          const progress = Math.max(0, 1 - ((scrollTop - startFade) / 1000));
          setScrollProgress(progress);
        }
      } else {
        setScrollProgress(1);
      }
    }
  }, [isHome, location.pathname]);

  return (
    <div className="fixed inset-0 bg-theme-body flex flex-col p-3 sm:p-5 font-sans transition-colors duration-200">
      <Navbar />
      <FlameWrap
        className="flex-1 w-full min-h-0 relative"
        intensity={0.5}
        height={170}
        spread={8}
        radius={40}
        speed={0.25}
        scale={0.75}
        turbulence={0.5}
        turbulenceScale={0.5}
        turbulenceReach={25}
        sparks={1.5}
        sparkSize={0.35}
        sparkDensity={1}
        sparkSpeed={1}
        rim={2.5}
        melt={0}
        distortion={10}
        smoke={1.5}
        ember={2}
        scorch={0}
        color={[1.0, 0.35, 0.12]}
        wipe={isHome ? 1 - scrollProgress : 0}
      >
        <main id="main-scroll-container" onScroll={handleScroll} className="h-full bg-theme-surface text-theme rounded-[2rem] sm:rounded-[2.5rem] overflow-y-auto relative w-full shadow-2xl transition-colors duration-200 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <Routes>
            <Route path="/" element={<Home />} />
            
            {/* Redirects for removed or renamed doc sections */}
            <Route path="/docs" element={<Navigate to="/docs/overview/introduction" replace />} />
            <Route path="/docs/ai-tools/*" element={<Navigate to="/docs/overview/introduction" replace />} />
            <Route path="/docs/ai-tools" element={<Navigate to="/docs/overview/introduction" replace />} />
            <Route path="/docs/vector-search/*" element={<Navigate to="/docs/overview/introduction" replace />} />
            <Route path="/docs/vector-search" element={<Navigate to="/docs/overview/introduction" replace />} />
            <Route path="/docs/torque/*" element={<Navigate to="/docs/overview/introduction" replace />} />
            <Route path="/docs/torque" element={<Navigate to="/docs/overview/introduction" replace />} />
            
            {/* Specific redirects for getting-started */}
            <Route path="/docs/getting-started/quick-start" element={<Navigate to="/docs/overview/getting-started" replace />} />
            <Route path="/docs/getting-started/installation" element={<Navigate to="/docs/overview/installation" replace />} />
            <Route path="/docs/getting-started/cli-commands" element={<Navigate to="/docs/overview/cli" replace />} />
            <Route path="/docs/getting-started/*" element={<Navigate to="/docs/overview/getting-started" replace />} />
            <Route path="/docs/getting-started" element={<Navigate to="/docs/overview/getting-started" replace />} />
            
            <Route path="/docs/authentication/*" element={<Navigate to="/docs/security-auth/authentication" replace />} />
            <Route path="/docs/authentication" element={<Navigate to="/docs/security-auth/authentication" replace />} />
            <Route path="/docs/collections/*" element={<Navigate to="/docs/data-services/database" replace />} />
            <Route path="/docs/collections" element={<Navigate to="/docs/data-services/database" replace />} />
            
            {/* Phase 2B-2 Consolidated doc routes (SPA client fallback) */}
            <Route path="/docs/api-reference/authentication" element={<Navigate to="/docs/api-reference/auth" replace />} />
            <Route path="/docs/realtime/disconnect-cleanup" element={<Navigate to="/docs/realtime/websocket" replace />} />
            <Route path="/docs/javascript-hooks/sandbox-limitations" element={<Navigate to="/docs/javascript-hooks/available-globals" replace />} />
            <Route path="/docs/file-storage/storage-drivers" element={<Navigate to="/docs/file-storage/s3-configuration" replace />} />
            <Route path="/docs/file-storage/thumbnail-generation" element={<Navigate to="/docs/file-storage/serve-files" replace />} />
            

            {/* New Docs Route */}
            <Route path="/docs/:group/:slug?" element={<Docs />} />
            
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectGuide />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/skills" element={<Skills />} />
            <Route path="/comparisons/pocketbase" element={<PocketBaseComparison />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </FlameWrap>
    </div>
  )
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <DocsDrawerProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppContent />
          </BrowserRouter>
        </DocsDrawerProvider>
      </ThemeProvider>
    </HelmetProvider>
  )
}

export default App
